/**
 * 3-way merge utility for text and binary files (spec §8).
 *
 * Text merge uses a line-level diff3 algorithm:
 *   1. Diff base→left and base→right using Myers LCS
 *   2. Walk hunks in parallel — only conflict when edits overlap
 *   3. Non-overlapping edits merge cleanly
 */

import { computeLineMapping } from './diff'

export interface MergeResult {
    merged: Uint8Array | string
    conflicted: boolean
}

// ── Internal types ─────────────────────────────────────────────

/** A contiguous hunk of edits from one side of the diff */
interface DiffHunk {
    /** Start line in base (inclusive) */
    baseStart: number
    /** End line in base (exclusive) */
    baseEnd: number
    /** Replacement lines from this side */
    lines: string[]
}

// ── Text merge ─────────────────────────────────────────────────

/**
 * Compute diff hunks between base and modified text.
 * Returns an array of DiffHunk describing regions that changed.
 */
function computeDiffHunks(baseLines: string[], modifiedLines: string[]): DiffHunk[] {
    // computeLineMapping gives us modified→base mapping
    const mapping = computeLineMapping(baseLines, modifiedLines)

    // Build a set of which base lines are "consumed" (matched) and in what order
    // We need to find contiguous regions of change.
    const hunks: DiffHunk[] = []

    let bi = 0  // current position in base
    let mi = 0  // current position in modified
    let mapIdx = 0

    while (bi < baseLines.length || mi < modifiedLines.length) {
        // Find the next match point
        let nextMatchBase: number | null = null
        let nextMatchMod: number | null = null

        // Scan mapping entries from current mapIdx to find next match at or after bi and mi
        for (let i = mapIdx; i < mapping.length; i++) {
            const m = mapping[i]
            if (m.oldIndex !== null && m.oldIndex >= bi && m.newIndex >= mi) {
                nextMatchBase = m.oldIndex
                nextMatchMod = m.newIndex
                mapIdx = i
                break
            }
        }

        if (nextMatchBase === null || nextMatchMod === null) {
            // No more matches — everything remaining is a hunk
            if (bi < baseLines.length || mi < modifiedLines.length) {
                hunks.push({
                    baseStart: bi,
                    baseEnd: baseLines.length,
                    lines: modifiedLines.slice(mi),
                })
            }
            break
        }

        // If there's a gap before the match, that's a hunk
        if (nextMatchBase > bi || nextMatchMod > mi) {
            hunks.push({
                baseStart: bi,
                baseEnd: nextMatchBase,
                lines: modifiedLines.slice(mi, nextMatchMod),
            })
        }

        // Advance past the match
        bi = nextMatchBase + 1
        mi = nextMatchMod + 1
        mapIdx++
    }

    return hunks
}

/**
 * Perform a line-level 3-way merge on text content.
 *
 * Algorithm (diff3):
 *   1. Diff base→left and base→right to get hunks
 *   2. Walk base lines; when a hunk applies from one side, apply it
 *   3. When hunks from both sides overlap the same base region, check
 *      if they produce identical edits — if so, apply once; otherwise conflict
 */
export function mergeText(base: string, left: string, right: string): MergeResult {
    // Fast paths
    if (left === right) return { merged: left, conflicted: false }
    if (left === base) return { merged: right, conflicted: false }
    if (right === base) return { merged: left, conflicted: false }

    const baseLines = base.split('\n')
    const leftLines = left.split('\n')
    const rightLines = right.split('\n')

    const leftHunks = computeDiffHunks(baseLines, leftLines)
    const rightHunks = computeDiffHunks(baseLines, rightLines)

    // Index hunks by their base region for fast lookup
    let li = 0  // index into leftHunks
    let ri = 0  // index into rightHunks
    const result: string[] = []
    let conflicted = false
    let bi = 0  // current base line

    while (bi <= baseLines.length) {
        // Get current hunks from each side (if they start at or before bi)
        let lh = li < leftHunks.length && leftHunks[li].baseStart <= bi ? leftHunks[li] : null
        let rh = ri < rightHunks.length && rightHunks[ri].baseStart <= bi ? rightHunks[ri] : null

        // Detect overlapping hunks: when only one side is active, check
        // whether the other side has a pending hunk whose base range overlaps.
        // Without this, staggered overlaps (left edits base[2..4], right edits
        // base[3..5]) silently apply both edits without flagging a conflict.
        if (lh && !rh && ri < rightHunks.length && rightHunks[ri].baseStart < lh.baseEnd) {
            rh = rightHunks[ri]
        }
        if (rh && !lh && li < leftHunks.length && leftHunks[li].baseStart < rh.baseEnd) {
            lh = leftHunks[li]
        }

        if (!lh && !rh) {
            // No hunks active — emit base line and advance
            if (bi < baseLines.length) {
                result.push(baseLines[bi])
            }
            bi++
            continue
        }

        if (lh && !rh) {
            // Only left changed this region
            result.push(...lh.lines)
            bi = lh.baseEnd
            li++
            continue
        }

        if (rh && !lh) {
            // Only right changed this region
            result.push(...rh.lines)
            bi = rh.baseEnd
            ri++
            continue
        }

        // Both sides have hunks — check for overlap
        if (lh && rh) {
            // Determine the overlapping region
            const overlapStart = Math.min(lh.baseStart, rh.baseStart)
            const overlapEnd = Math.max(lh.baseEnd, rh.baseEnd)

            // Check if both sides made identical edits
            const leftText = lh.lines.join('\n')
            const rightText = rh.lines.join('\n')

            if (leftText === rightText && lh.baseStart === rh.baseStart && lh.baseEnd === rh.baseEnd) {
                // Identical edit — apply once
                result.push(...lh.lines)
            } else {
                // True conflict
                conflicted = true
                result.push('<<<<<<< LOCAL')
                result.push(...lh.lines)
                result.push('=======')
                result.push(...rh.lines)
                result.push('>>>>>>> REMOTE')
            }

            bi = overlapEnd
            li++
            ri++
            continue
        }
    }

    // Remove trailing empty line added by split if original didn't end with newline
    const merged = result.join('\n')

    return { merged, conflicted }
}

// ── Binary merge ───────────────────────────────────────────────

/**
 * Perform a merge on binary content or files where we don't do 3-way merge.
 * (Spec §8.2: Binary: latest mtime wins, if equal remote wins)
 */
export function mergeBinary(
    left: { data: Uint8Array; mtime: number },
    right: { data: Uint8Array; mtime: number },
    isRemoteRight: boolean
): MergeResult {
    if (left.mtime > right.mtime) {
        return { merged: left.data, conflicted: false }
    } else if (right.mtime > left.mtime) {
        return { merged: right.data, conflicted: false }
    } else {
        // Equal mtime, remote wins per spec
        return { merged: isRemoteRight ? right.data : left.data, conflicted: false }
    }
}

// ── Chunk-level binary merge (v3 manifests) ────────────────────

/** A chunk reference from a v3 manifest */
export interface ChunkRef {
    hash: string
    plainSize: number
}

/** Result of merging chunk manifests */
export interface ChunkMergeResult {
    /** Merged chunk sequence */
    chunks: ChunkRef[]
    /** Whether any conflict occurred (overlapping edits) */
    conflicted: boolean
    /** Whether local changes were kept in the merge */
    anyLocalChange: boolean
}

/**
 * 3-way merge on chunk hash sequences from v3 manifests.
 *
 * Algorithm:
 *   1. LCS between base→local and base→remote chunk hash sequences
 *   2. Walk hunks in parallel (same as text diff3)
 *   3. Non-overlapping chunk edits → auto-merge
 *   4. Overlapping chunk edits → remote wins (§8.2)
 *
 * This enables fine-grained binary merge:
 *   - Edit page 4 of 1000-page SQLite DB + someone else edits page 500 → merges cleanly
 *   - Edit same region → remote wins (no garbled data)
 *
 * The merged manifest should have `plaintextSha256 = null` since we can't
 * compute the whole-file hash without re-reading all chunk data.
 */
export function mergeChunkedBinary(
    base: ChunkRef[],
    local: ChunkRef[],
    remote: ChunkRef[],
): ChunkMergeResult {
    // Fast paths
    const localHashes = local.map(c => c.hash)
    const remoteHashes = remote.map(c => c.hash)
    const baseHashes = base.map(c => c.hash)

    if (localHashes.join(',') === remoteHashes.join(',')) {
        // Both sides identical — no merge needed
        return { chunks: [...local], conflicted: false, anyLocalChange: false }
    }

    if (localHashes.join(',') === baseHashes.join(',')) {
        // Local unchanged → take remote
        return { chunks: [...remote], conflicted: false, anyLocalChange: false }
    }

    if (remoteHashes.join(',') === baseHashes.join(',')) {
        // Remote unchanged → take local
        return { chunks: [...local], conflicted: false, anyLocalChange: true }
    }

    // Compute diff hunks for both sides
    const localHunks = computeChunkDiffHunks(baseHashes, localHashes, base, local)
    const remoteHunks = computeChunkDiffHunks(baseHashes, remoteHashes, base, remote)

    // Walk hunks in parallel (same diff3 walk as text merge)
    let li = 0
    let ri = 0
    let bi = 0
    const result: ChunkRef[] = []
    let conflicted = false
    let anyLocalChange = false

    while (bi <= base.length) {
        let lh = li < localHunks.length && localHunks[li].baseStart <= bi ? localHunks[li] : null
        let rh = ri < remoteHunks.length && remoteHunks[ri].baseStart <= bi ? remoteHunks[ri] : null

        // Detect overlapping hunks: when only one side is active, check
        // whether the other side has a pending hunk whose base range overlaps.
        if (lh && !rh && ri < remoteHunks.length && remoteHunks[ri].baseStart < lh.baseEnd) {
            rh = remoteHunks[ri]
        }
        if (rh && !lh && li < localHunks.length && localHunks[li].baseStart < rh.baseEnd) {
            lh = localHunks[li]
        }

        if (!lh && !rh) {
            if (bi < base.length) {
                result.push(base[bi])
            }
            bi++
            continue
        }

        if (lh && !rh) {
            result.push(...lh.chunks)
            bi = lh.baseEnd
            li++
            anyLocalChange = true
            continue
        }

        if (rh && !lh) {
            result.push(...rh.chunks)
            bi = rh.baseEnd
            ri++
            continue
        }

        // Both sides have hunks
        if (lh && rh) {
            const overlapEnd = Math.max(lh.baseEnd, rh.baseEnd)

            // Check if identical edits
            const localChunkHashes = lh.chunks.map(c => c.hash).join(',')
            const remoteChunkHashes = rh.chunks.map(c => c.hash).join(',')

            if (localChunkHashes === remoteChunkHashes && lh.baseStart === rh.baseStart && lh.baseEnd === rh.baseEnd) {
                // Identical edit — apply once
                result.push(...lh.chunks)
            } else {
                // Overlapping conflict — remote wins (§8.2)
                result.push(...rh.chunks)
                conflicted = true
            }

            bi = overlapEnd
            li++
            ri++
            continue
        }
    }

    return { chunks: result, conflicted, anyLocalChange }
}

/** Internal diff hunk for chunk sequences */
interface ChunkDiffHunk {
    baseStart: number
    baseEnd: number
    chunks: ChunkRef[]
}

/** Compute diff hunks between base and modified chunk sequences */
function computeChunkDiffHunks(
    baseHashes: string[],
    modifiedHashes: string[],
    _baseChunks: ChunkRef[],
    modifiedChunks: ChunkRef[],
): ChunkDiffHunk[] {
    const mapping = computeLineMapping(baseHashes, modifiedHashes)

    const hunks: ChunkDiffHunk[] = []
    let bi = 0
    let mi = 0
    let mapIdx = 0

    while (bi < baseHashes.length || mi < modifiedHashes.length) {
        let nextMatchBase: number | null = null
        let nextMatchMod: number | null = null

        for (let i = mapIdx; i < mapping.length; i++) {
            const m = mapping[i]
            if (m.oldIndex !== null && m.oldIndex >= bi && m.newIndex >= mi) {
                nextMatchBase = m.oldIndex
                nextMatchMod = m.newIndex
                mapIdx = i
                break
            }
        }

        if (nextMatchBase === null || nextMatchMod === null) {
            if (bi < baseHashes.length || mi < modifiedHashes.length) {
                hunks.push({
                    baseStart: bi,
                    baseEnd: baseHashes.length,
                    chunks: modifiedChunks.slice(mi),
                })
            }
            break
        }

        if (nextMatchBase > bi || nextMatchMod > mi) {
            hunks.push({
                baseStart: bi,
                baseEnd: nextMatchBase,
                chunks: modifiedChunks.slice(mi, nextMatchMod),
            })
        }

        bi = nextMatchBase + 1
        mi = nextMatchMod + 1
        mapIdx++
    }

    return hunks
}

